import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {ClientService} from 'src/app/services/client.service';
import {TranslateService} from '@ngx-translate/core';
import {ValidateDate} from '../../../validators/date.validator';
import {ValidatePassedDate} from '../../../validators/past-date.validator';
import {CourtService} from '../../../services/court.service';
import * as moment from 'moment';
import {NotificationService} from '../../../services/notification.service';
import EMAIL_REGEX from '../../../validators/regex/email.regex';
import BMCB_REGEX from '../../../validators/regex/bmcb.regex';
import {CourtDTO} from '../../../model/courtDTO';
import {ClientDTO} from '../../../model/clientDTO';
import {Level} from '../../shared/notification/Level';
import {ConfirmDTO} from '../../shared/notification/ConfirmDTO';

@Component({
  templateUrl: './add-client.component.html',
  styleUrls: ['./add-client.component.scss']
})
export class AddClientComponent implements OnInit {

  form: FormGroup;
  submitted = false;
  courts: CourtDTO[];
  years = [];
  isLoading;

  constructor(private builder: FormBuilder,
              private clientService: ClientService,
              private router: Router,
              private courtService: CourtService,
              private notificationService: NotificationService,
              public translate: TranslateService) {
    translate.use('nl');
    this.form = this.getCleanForm();
  }

  get f() {
    return this.form.controls;
  }

  ngOnInit() {
    this.courtService.getAll().subscribe(res => this.courts = res);
    this.addConditionalValidationToEmail();
  }

  private addConditionalValidationToEmail() {
    this.form.get('showClientEmailField').valueChanges.subscribe(showEmailField => {
      const control = this.form.controls['email'];
      if (showEmailField) {
        control.setValidators([Validators.required, Validators.pattern(EMAIL_REGEX), Validators.maxLength(255)]);
        control.updateValueAndValidity();
      } else {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });
  }

  submit(value) {
    this.submitted = true;
    if (this.form.invalid || this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.clientService.create(value).subscribe(client => {
      this.isLoading = false;
      this.notificationService.success('bewind aangemaakt');
      if (value.addSecondUser) {
        this.showSecondBewindvoerderModal(client);
      } else {
        this.router.navigate([`/bewind/${client.id}`]);
      }
    }, error => {
      this.isLoading = false;
      this.notificationService.errorResponse(error);
    });
  }

  showSecondBewindvoerderModal(client: ClientDTO) {
    const text: ConfirmDTO = {
      header: 'Wilt u de tweede bewindvoerder nu toevoegen?',
      ok:  'bewindvoerder toevoegen',
      cancel: 'annuleren',
      level: Level.INFO
    };
    this.notificationService.confirm(text)
      .then(() => {
        window.scrollTo(0, 0);
        setTimeout(() => this.router.navigate([`bewind/${client.id}/tweede-bewindvoerder/toevoegen`]), 150);
      })
      .catch(() => this.router.navigate([`/bewind/${client.id}`]));
  }

  olderThan(value, min) {
    const date = moment(value, 'DD-MM-YYYY', true);
    return date.isValid() && date.year() <= new Date().getFullYear() - min;
  }

  updateYears() {
    const date = moment(this.f.startDate.value, 'DD-MM-YYYY', true);
    if (date.isValid()) {
      if (this.f.catchingUpYear.value < date.year()) {
        this.f.catchingUpYear.setValue(null);
      }
      this.years = this.getYears(date.year());
    }
  }

  getPreviousVjeDeadline(startDate) {
    let increment = 0;
    do {
      increment += 5;
    }
    while (this.olderThan(startDate, increment));
    increment -= 5;
    return moment(startDate, 'DD-MM-YYYY', true).add(increment, 'years').year();
  }

  private getCleanForm() {
    return this.builder.group({
      'firstNameInitials': ['', [Validators.required, Validators.maxLength(255)]],
      'firstName': ['', [Validators.required, Validators.maxLength(255)]],
      'insertion': ['', [Validators.maxLength(255)]],
      'lastName': ['', [Validators.required, Validators.maxLength(255)]],
      'dateOfBirth': [null, [Validators.required, ValidateDate]],

      'showClientEmailField': [false],
      'email': [''], // see addConditionalValidationToEmail()

      'street': ['', [Validators.required, Validators.maxLength(255)]],
      'houseNumber': ['', [Validators.required, Validators.maxLength(255)]],
      'houseNumberSuffix': ['', [Validators.maxLength(255)]],
      'zipCode': ['', [Validators.required, Validators.maxLength(255)]],
      'city': ['', [Validators.required, Validators.maxLength(255)]],

      'courtId': ['', Validators.required],
      'caseType': [null, [Validators.required]],
      'caseNumber': ['', [Validators.required, Validators.pattern(BMCB_REGEX), Validators.maxLength(255)]],
      'startDate': [null, [Validators.required, ValidateDate, ValidatePassedDate]],

      'bbToBeMade': [false],
      'catchingUp': [false],
      'catchingUpYear': [''],
      'previousVjeToBeMade': [false],

      'showEmailInReports': [false],
      'addSecondUser': [false]
    });
  }

  private getYears = from => {
    const options = [];
    for (let currentYear = this.getPreviousYear(), i = from;
         currentYear >= i;
         currentYear--) {
      options.push({value: currentYear});
    }
    return options;
  }


  getPreviousYear() {
    return new Date().getFullYear() - 1;
  }
}
